"use client";

import { useState } from "react";
import { ParagraphHeading } from "../shared/ParagraphHeading";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type { ParagraphPresentation as ParagraphPresentationData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { Attachments } from "../shared/Attachments";
import styles from "../Paragraph.module.css";

/**
 * Layout 15 - `PARAGRAPH_BIG_PHOTO_LAYOUT`: jedno duże zdjęcie prezentowane na
 * pełną szerokość strony.
 *
 * **Source:** `img.full?.url` (oryginał z backendu, NIE miniatura `img.url`) -
 * zdjęcie ma być duże wizualnie, więc nie ma sensu pokazywać przyciętej wersji.
 *
 * **Full-bleed:** klasa `.bigPhoto` w Paragraph.module.css rozciąga element poza
 * padding parent <main> (przez `margin-inline: calc(50% - 50vw)`) do krawędzi
 * viewport. Lightbox na klik dla zoom.
 */
export function ParagraphBigPhoto({
  paragraph,
  t,
}: {
  paragraph: ParagraphPresentationData;
  t: ParagraphTranslations;
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  const images = paragraph.images ?? [];

  if (images.length === 0) {
    return (
      <section className={styles.paragraph}>
        <ParagraphHeading name={paragraph.name} centerHeader={paragraph.center_header} />
        <div className={styles.empty}>{t.gallery.empty}</div>
        <Attachments files={paragraph.attachments} t={t} />
      </section>
    );
  }

  const slides = images.map((img) => ({
    src: img.full?.url || img.url,
    width: img.full?.width ?? img.width ?? undefined,
    height: img.full?.height ?? img.height ?? undefined,
    alt: img.alt ?? img.description ?? "",
    description: img.description ?? undefined,
  }));

  return (
    <section className={styles.paragraph}>
      <ParagraphHeading name={paragraph.name} centerHeader={paragraph.center_header} />
      <div className={styles.bigPhoto}>
        {images.map((img, i) => (
          <figure key={img.id} className={styles.bigPhotoFigure}>
            <button
              type="button"
              className={styles.bigPhotoButton}
              onClick={() => setOpenAt(i)}
              aria-label={img.alt ?? img.description ?? t.gallery.openImage}
            >
              <img
                src={img.full?.url || img.url}
                alt={img.alt ?? img.description ?? ""}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                className={styles.bigPhotoImg}
              />
            </button>
            {img.description && (
              <figcaption className={styles.bigPhotoDescription}>{img.description}</figcaption>
            )}
          </figure>
        ))}
      </div>
      <Lightbox
        open={openAt !== null}
        index={openAt ?? 0}
        close={() => setOpenAt(null)}
        slides={slides}
      />
      <Attachments files={paragraph.attachments} t={t} />
    </section>
  );
}
